import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import Grid from '../common/Grid';
import {
  Person,
  Email,
  Badge,
  CalendarToday,
  Edit,
  Logout,
  Star,
  StarBorder,
  TrendingUp,
  Storage,
  Assessment,
  ExpandMore,
  Upgrade,
  Cancel,
  Info,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { logout, getCurrentUser } from '../../store/slices/authSlice';
import {
  fetchSubscription,
  startTrial,
  cancelSubscription,
  fetchUsage
} from '../../store/slices/subscriptionSlice';

const UserProfile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { subscription, usage, limits, isLoading, error } = useSelector((state: RootState) => state.subscription);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: user?.name || '',
    company: user?.company || '',
  });

  // Fetch subscription data on component mount
  useEffect(() => {
    if (user) {
      dispatch(fetchSubscription());
      dispatch(fetchUsage(undefined));
    }
  }, [dispatch, user]);

  // Add periodic refresh to check for email verification updates
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      // Only refresh if user is not verified (to catch admin verification)
      if (user && !user.emailVerified) {
        dispatch(getCurrentUser());
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(refreshInterval);
  }, [dispatch, user?.emailVerified]);

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          User information not available. Please try logging in again.
        </Alert>
      </Box>
    );
  }

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleEditProfile = () => {
    setEditFormData({
      name: user.name || '',
      company: user.company || '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveProfile = () => {
    // TODO: Implement profile update API call
    console.log('Saving profile:', editFormData);
    setEditDialogOpen(false);
  };

  const handleStartTrial = async () => {
    try {
      await dispatch(startTrial()).unwrap();
      // Refresh subscription status
      dispatch(fetchSubscription());
    } catch (error) {
      console.error('Failed to start trial:', error);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await dispatch(cancelSubscription()).unwrap();
      // Refresh subscription status
      dispatch(fetchSubscription());
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    }
  };

  const getInitials = () => {
    const name = user.name || '';
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase() || 'U';
  };

  const getStatusColor = (verified: boolean) => {
    return verified ? 'success' : 'warning';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'FREE':
        return 'default';
      case 'PROFESSIONAL':
        return 'primary';
      case 'ENTERPRISE':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'FREE':
        return <StarBorder />;
      case 'PROFESSIONAL':
        return <Star />;
      case 'ENTERPRISE':
        return <TrendingUp />;
      default:
        return <StarBorder />;
    }
  };

  const getUsagePercentage = (used: number, limit: number) => {
    if (limit === -1) return 0; // Unlimited
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'primary';
  };

  const isTrialActive = subscription?.status === 'TRIALING';
  const isTrialEligible = subscription?.tier === 'FREE' && !subscription?.trialEnd;
  const daysUntilTrialEnd = subscription?.trialEnd
    ? Math.ceil((new Date(subscription.trialEnd).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '2rem',
                mr: 3,
              }}
            >
              {getInitials()}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" gutterBottom>
                {user.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label={user.emailVerified ? 'Verified' : 'Unverified'}
                  color={getStatusColor(user.emailVerified) as any}
                  size="small"
                  icon={<Badge />}
                />
                {user.company && (
                  <Chip
                    label={user.company}
                    color="default"
                    size="small"
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleEditProfile}
              >
                Edit Profile
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Logout />}
                onClick={handleLogout}
              >
                Logout
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Profile Information */}
          <Grid container spacing={3}>
            <Grid xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Email sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email Address
                  </Typography>
                  <Typography variant="body1">{user.email}</Typography>
                </Box>
              </Box>
            </Grid>

            <Grid xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Full Name
                  </Typography>
                  <Typography variant="body1">{user.name}</Typography>
                </Box>
              </Box>
            </Grid>

            {user.company && (
              <Grid xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Person sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Company
                    </Typography>
                    <Typography variant="body1">{user.company}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarToday sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Member Since
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(user.createdAt)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarToday sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(user.updatedAt)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Subscription Information */}
          <Typography variant="h6" gutterBottom>
            Subscription & Usage
          </Typography>

          {isLoading ? (
            <Box sx={{ mb: 3 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Loading subscription information...
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              Failed to load subscription information: {error}
            </Alert>
          ) : subscription ? (
            <Box sx={{ mb: 3 }}>
              {/* Subscription Status Card */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTierIcon(subscription.tier)}
                      <Typography variant="h6">
                        {subscription.tier} Plan
                      </Typography>
                      <Chip
                        label={subscription.status}
                        color={subscription.status === 'ACTIVE' ? 'success' : subscription.status === 'TRIALING' ? 'info' : 'default'}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {isTrialEligible && (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<Star />}
                          onClick={handleStartTrial}
                          size="small"
                        >
                          Start 14-Day Trial
                        </Button>
                      )}

                      {isTrialActive && (
                        <Button
                          variant="outlined"
                          color="warning"
                          startIcon={<Cancel />}
                          onClick={handleCancelSubscription}
                          size="small"
                        >
                          Cancel Trial
                        </Button>
                      )}

                      {subscription.tier !== 'ENTERPRISE' && (
                        <Button
                          variant="outlined"
                          startIcon={<Upgrade />}
                          size="small"
                        >
                          Upgrade
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {isTrialActive && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Trial Active:</strong> {daysUntilTrialEnd} days remaining in your Professional trial.
                        {daysUntilTrialEnd <= 3 && ' Your trial is ending soon!'}
                      </Typography>
                    </Alert>
                  )}

                  <Typography variant="body2" color="text.secondary">
                    Current period: {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                  </Typography>
                </CardContent>
              </Card>

              {/* Usage Statistics */}
              {usage && limits && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Usage Statistics</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" color={getUsageColor(getUsagePercentage(usage.recipesCreated, limits.recipesPerMonth))}>
                              {usage.recipesCreated}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Recipes Created
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {limits.recipesPerMonth === -1 ? 'Unlimited' : `of ${limits.recipesPerMonth} this month`}
                            </Typography>
                            {limits.recipesPerMonth !== -1 && (
                              <LinearProgress
                                variant="determinate"
                                value={getUsagePercentage(usage.recipesCreated, limits.recipesPerMonth)}
                                color={getUsageColor(getUsagePercentage(usage.recipesCreated, limits.recipesPerMonth))}
                                sx={{ mt: 1 }}
                              />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" color={getUsageColor(getUsagePercentage(usage.waterProfilesCreated, limits.waterProfilesTotal))}>
                              {usage.waterProfilesCreated}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Water Profiles
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {limits.waterProfilesTotal === -1 ? 'Unlimited' : `of ${limits.waterProfilesTotal} total`}
                            </Typography>
                            {limits.waterProfilesTotal !== -1 && (
                              <LinearProgress
                                variant="determinate"
                                value={getUsagePercentage(usage.waterProfilesCreated, limits.waterProfilesTotal)}
                                color={getUsageColor(getUsagePercentage(usage.waterProfilesCreated, limits.waterProfilesTotal))}
                                sx={{ mt: 1 }}
                              />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" color={getUsageColor(getUsagePercentage(usage.customSaltsCreated, limits.customSaltsTotal))}>
                              {usage.customSaltsCreated}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Custom Salts
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {limits.customSaltsTotal === -1 ? 'Unlimited' : `of ${limits.customSaltsTotal} total`}
                            </Typography>
                            {limits.customSaltsTotal !== -1 && (
                              <LinearProgress
                                variant="determinate"
                                value={getUsagePercentage(usage.customSaltsCreated, limits.customSaltsTotal)}
                                color={getUsageColor(getUsagePercentage(usage.customSaltsCreated, limits.customSaltsTotal))}
                                sx={{ mt: 1 }}
                              />
                            )}
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid xs={12} sm={6} md={3}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" color={getUsageColor(getUsagePercentage(usage.storageUsedMB, limits.storageMaxMB))}>
                              {usage.storageUsedMB.toFixed(1)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Storage (MB)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              of {limits.storageMaxMB} MB
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={getUsagePercentage(usage.storageUsedMB, limits.storageMaxMB)}
                              color={getUsageColor(getUsagePercentage(usage.storageUsedMB, limits.storageMaxMB))}
                              sx={{ mt: 1 }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          ) : null}

          <Divider sx={{ my: 3 }} />

          {/* Account Statistics */}
          <Typography variant="h6" gutterBottom>
            Account Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="primary">
                    {usage?.formulationsCreated || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Formulations Created
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="primary">
                    {usage?.waterProfilesCreated || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Water Profiles
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" color="primary">
                    {usage?.customSaltsCreated || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Custom Salts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={editFormData.name}
                onChange={(e) =>
                  setEditFormData(prev => ({ ...prev, name: e.target.value }))
                }
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label="Company (Optional)"
                value={editFormData.company}
                onChange={(e) =>
                  setEditFormData(prev => ({ ...prev, company: e.target.value }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveProfile} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfile;
